import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Star, CheckCircle, AlertCircle, Loader2, XCircle, User, Mail, Award, ChevronLeft, ChevronRight } from 'lucide-react';
import MagnetixLogo from '@/assets/MainLogo/MKraftLogoDark.png';
import httpClient from '@/services/httpClient';
import './PublicFeedbackForm.css';

interface Question {
  id: number;
  type: 'short_text' | 'paragraph' | 'multiple_choice_single' | 'multiple_choice_multi' | 'star_rating' | 'slider';
  text: string;
  required: boolean;
  options?: string[];
}

interface FormData {
  id: number;
  name: string;
  description: string;
  type: 'feedback' | 'satisfaction' | 'assessment';
  assessmentType?: 'objective' | 'subjective' | 'both';
  collectName: boolean;
  collectEmail: boolean;
  showCorrectAnswers: boolean;
  showReport: boolean;
  questions: Question[];
}

interface AssessmentResult {
  questionId: number;
  questionText: string;
  questionType?: string;
  // Objective fields
  options?: string[];
  userSelectedIndices?: number[];
  correctIndices?: number[];
  isCorrect?: boolean;
  // Subjective/AI fields
  isSubjective?: boolean;
  userAnswerText?: string;
  aiScore?: number | null;
  aiMaxScore?: number | null;
  aiFeedback?: string | null;
}

interface AssessmentResults {
  score: number;
  maxScore: number;
  percentage: number;
  assessmentType?: 'objective' | 'subjective' | 'both';
  showCorrectAnswers: boolean;
  showReport: boolean;
  results?: AssessmentResult[];
  respondentName?: string | null;
  respondentEmail?: string | null;
}

interface Answer {
  questionId: number;
  text?: string;
  options?: string[];
  rating?: number;
}

type FormStatus = 'loading' | 'ready' | 'submitting' | 'success' | 'error' | 'closed';

const PublicFeedbackForm: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [formData, setFormData] = useState<FormData | null>(null);
  const [status, setStatus] = useState<FormStatus>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  const [respondentName, setRespondentName] = useState('');
  const [respondentEmail, setRespondentEmail] = useState('');
  const [answers, setAnswers] = useState<Record<number, Answer>>({});
  const [assessmentResults, setAssessmentResults] = useState<AssessmentResults | null>(null);

  // Stepper state (assessment only)
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  const isAssessment = formData?.type === 'assessment';
  const totalQuestions = formData?.questions.length || 0;

  useEffect(() => {
    fetchForm();
  }, [slug]);

  const fetchForm = async () => {
    try {
      setStatus('loading');
      setCurrentQuestionIndex(0);
      const response = await httpClient.get<{ success: boolean; data: any; error?: string }>(
        `/public/feedback/${slug}`
      );

      if (response.success && response.data) {
        const form = response.data;
        const questions = (form.questions || []).map((q: any) => ({
          id: q.id,
          type: q.question_type,
          text: q.question_text,
          required: q.is_required === 1,
          options: q.options ? (typeof q.options === 'string' ? JSON.parse(q.options) : q.options) : undefined
        }));

        setFormData({
          id: form.id,
          name: form.name,
          description: form.description || '',
          type: form.type || 'feedback',
          assessmentType: form.assessmentType || form.assessment_type || 'objective',
          collectName: form.collect_name === 1 || form.collectName === true,
          collectEmail: form.collect_email === 1 || form.collectEmail === true,
          showCorrectAnswers: form.showCorrectAnswers !== false,
          showReport: form.showReport === true,
          questions
        });

        // Do NOT pre-initialize slider answers — let the user interact first

        setStatus('ready');
      } else {
        setErrorMessage(response.error || 'Form not found');
        setStatus('error');
      }
    } catch (error: any) {
      if (error?.response?.status === 410) {
        setErrorMessage('This form is no longer accepting responses.');
        setStatus('closed');
      } else {
        setErrorMessage(error?.message || 'Failed to load form');
        setStatus('error');
      }
    }
  };

  const generateBrowserFingerprint = (): string => {
    const nav = window.navigator;
    const screen = window.screen;
    const fingerprint = [
      nav.userAgent,
      nav.language,
      screen.colorDepth,
      screen.width + 'x' + screen.height,
      new Date().getTimezoneOffset(),
      nav.cookieEnabled ? '1' : '0',
      nav.hardwareConcurrency || 0
    ].join('|');

    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(16);
  };

  const handleAnswerChange = (questionId: number, value: string | string[] | number, type: Question['type']) => {
    setAnswers(prev => {
      const newAnswer: Answer = { questionId };

      if (type === 'star_rating') {
        newAnswer.rating = value as number;
      } else if (type === 'slider') {
        newAnswer.text = String(value);
      } else if (type === 'multiple_choice_multi') {
        newAnswer.options = value as string[];
      } else if (type === 'multiple_choice_single') {
        newAnswer.options = [value as string];
      } else {
        newAnswer.text = value as string;
      }

      return { ...prev, [questionId]: newAnswer };
    });
  };

  const handleMultiOptionToggle = (questionId: number, option: string) => {
    setAnswers(prev => {
      const currentOptions = prev[questionId]?.options || [];
      const newOptions = currentOptions.includes(option)
        ? currentOptions.filter(o => o !== option)
        : [...currentOptions, option];

      return {
        ...prev,
        [questionId]: { questionId, options: newOptions }
      };
    });
  };

  const validateRespondentFields = (): boolean => {
    if (!formData) return true;
    if (formData.collectName && !respondentName.trim()) {
      setErrorMessage('Please enter your name.');
      return false;
    }
    if (formData.collectEmail && !respondentEmail.trim()) {
      setErrorMessage('Please enter your email.');
      return false;
    }
    return true;
  };

  const validateQuestion = (question: Question): boolean => {
    if (!question.required) return true;
    const answer = answers[question.id];
    if (!answer) return false;
    if (question.type === 'star_rating' && !answer.rating) return false;
    if (question.type === 'slider' && (!answer.text || answer.text.trim() === '')) return false;
    if ((question.type === 'multiple_choice_single' || question.type === 'multiple_choice_multi')
        && (!answer.options || answer.options.length === 0)) return false;
    if ((question.type === 'short_text' || question.type === 'paragraph')
        && (!answer.text || answer.text.trim() === '')) return false;
    return true;
  };

  const validateCurrentStep = (): boolean => {
    if (!formData) return false;

    // Validate name/email on first step
    if (currentQuestionIndex === 0 && !validateRespondentFields()) {
      return false;
    }

    // Validate current question
    const question = formData.questions[currentQuestionIndex];
    if (question && !validateQuestion(question)) {
      const msg = question.type === 'star_rating'
        ? 'Please provide a rating.'
        : (question.type === 'multiple_choice_single' || question.type === 'multiple_choice_multi')
          ? 'Please select an option.'
          : 'Please answer this question before continuing.';
      setErrorMessage(msg);
      return false;
    }

    setErrorMessage('');
    return true;
  };

  const handleNext = () => {
    if (validateCurrentStep()) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    setErrorMessage('');
    setCurrentQuestionIndex(prev => prev - 1);
  };

  const validateForm = (): boolean => {
    if (!formData) return false;

    if (!validateRespondentFields()) return false;

    for (const question of formData.questions) {
      if (!validateQuestion(question)) {
        setErrorMessage('Please answer all required questions.');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // In stepper mode, if not on the last question, advance instead of submitting
    if (isAssessment && currentQuestionIndex < totalQuestions - 1) {
      handleNext();
      return;
    }

    // In stepper mode, validate current step first
    if (isAssessment && !validateCurrentStep()) return;

    if (!validateForm()) {
      if (!errorMessage) setErrorMessage('Please answer all required questions.');
      return;
    }

    setStatus('submitting');
    setErrorMessage('');

    try {
      const response = await httpClient.post(`/public/feedback/${slug}/submit`, {
        browserFingerprint: generateBrowserFingerprint(),
        respondentName: respondentName || null,
        respondentEmail: respondentEmail || null,
        answers: Object.values(answers).map(a => ({
          questionId: a.questionId,
          text: a.text || null,
          options: a.options || null,
          rating: a.rating || null
        }))
      });

      if (response.success) {
        if (response.score !== undefined) {
          const reportEnabled = response.showReport === true;
          setAssessmentResults({
            score: response.score,
            maxScore: response.maxScore,
            percentage: parseFloat(response.percentage),
            assessmentType: response.assessmentType || 'objective',
            showCorrectAnswers: reportEnabled ? (response.showCorrectAnswers !== false) : false,
            showReport: reportEnabled,
            results: reportEnabled ? (response.results || undefined) : undefined,
            respondentName: response.respondentName,
            respondentEmail: response.respondentEmail
          });
        }
        setStatus('success');
      } else {
        setErrorMessage(response.error || 'Failed to submit response');
        setStatus('ready');
      }
    } catch (error: any) {
      if (error?.response?.status === 409) {
        setErrorMessage('You have already submitted a response to this form.');
      } else if (error?.response?.status === 410) {
        setErrorMessage('This form is no longer accepting responses.');
        setStatus('closed');
      } else {
        setErrorMessage(error?.message || 'Failed to submit response');
      }
      setStatus('ready');
    }
  };

  const getScoreMessage = (percentage: number): string => {
    if (percentage >= 90) return 'Excellent work!';
    if (percentage >= 70) return 'Good job!';
    if (percentage >= 50) return 'Keep practicing!';
    return 'Better luck next time!';
  };

  const renderQuestion = (question: Question, index: number) => {
    const answer = answers[question.id];

    return (
      <div key={question.id} className="public-feedback-question">
        <div className="public-feedback-question-header">
          <span className="public-feedback-question-number">{index + 1}</span>
          <label className="public-feedback-question-text">
            {question.text}
            {question.required && <span className="public-feedback-required">*</span>}
          </label>
        </div>

        <div className="public-feedback-question-input">
          {question.type === 'short_text' && (
            <input
              type="text"
              className="public-feedback-input"
              placeholder="Your answer..."
              value={answer?.text || ''}
              onChange={(e) => handleAnswerChange(question.id, e.target.value, question.type)}
            />
          )}

          {question.type === 'paragraph' && (
            <textarea
              className="public-feedback-textarea"
              placeholder="Your answer..."
              rows={4}
              value={answer?.text || ''}
              onChange={(e) => handleAnswerChange(question.id, e.target.value, question.type)}
            />
          )}

          {question.type === 'multiple_choice_single' && question.options && (
            <div className="public-feedback-options">
              {question.options.map((option, i) => (
                <label key={i} className="public-feedback-radio-option">
                  <input
                    type="radio"
                    name={`question-${question.id}`}
                    checked={answer?.options?.[0] === option}
                    onChange={() => handleAnswerChange(question.id, option, question.type)}
                  />
                  <span className="public-feedback-radio-custom" />
                  <span className="public-feedback-option-text">{option}</span>
                </label>
              ))}
            </div>
          )}

          {question.type === 'multiple_choice_multi' && question.options && (
            <div className="public-feedback-options">
              {question.options.map((option, i) => (
                <label key={i} className="public-feedback-checkbox-option">
                  <input
                    type="checkbox"
                    checked={answer?.options?.includes(option) || false}
                    onChange={() => handleMultiOptionToggle(question.id, option)}
                  />
                  <span className="public-feedback-checkbox-custom" />
                  <span className="public-feedback-option-text">{option}</span>
                </label>
              ))}
            </div>
          )}

          {question.type === 'star_rating' && (
            <div className="public-feedback-stars">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  className={`public-feedback-star ${(answer?.rating || 0) >= star ? 'active' : ''}`}
                  onClick={() => handleAnswerChange(question.id, star, question.type)}
                >
                  <Star size={32} fill={(answer?.rating || 0) >= star ? '#F97316' : 'none'} />
                </button>
              ))}
            </div>
          )}

          {question.type === 'slider' && (() => {
            const sliderMin = question.options?.[0] ? Number(question.options[0]) : 1;
            const sliderMax = question.options?.[1] ? Number(question.options[1]) : 10;
            const defaultVal = sliderMin + Math.floor((sliderMax - sliderMin) / 2);
            const hasInteracted = answer?.text !== undefined && answer.text !== '';
            const currentVal = hasInteracted ? Number(answer.text) : defaultVal;

            return (
              <div className="public-feedback-slider-container">
                {!hasInteracted && (
                  <p style={{ fontSize: '13px', color: '#9ca3af', margin: '0 0 8px' }}>
                    Drag the slider to select a value
                  </p>
                )}
                <input
                  type="range"
                  min={sliderMin}
                  max={sliderMax}
                  value={currentVal}
                  className="public-feedback-slider"
                  style={{ opacity: hasInteracted ? 1 : 0.5 }}
                  onChange={(e) => {
                    handleAnswerChange(question.id, e.target.value, question.type);
                  }}
                />
                <div className="public-feedback-slider-labels">
                  <span>{sliderMin}</span>
                  <span className="public-feedback-slider-value">{hasInteracted ? currentVal : '-'}</span>
                  <span>{sliderMax}</span>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    );
  };

  if (status === 'loading') {
    return (
      <div className={`public-feedback-container ${isAssessment ? 'assessment-mode' : ''}`}>
        <div className="public-feedback-loading">
          <Loader2 size={48} className="public-feedback-spinner" />
          <p>Loading form...</p>
        </div>
      </div>
    );
  }

  if (status === 'error' || status === 'closed') {
    return (
      <div className={`public-feedback-container ${isAssessment ? 'assessment-mode' : ''}`}>
        <div className="public-feedback-error">
          <AlertCircle size={48} />
          <h2>{status === 'closed' ? 'Form Closed' : 'Form Not Available'}</h2>
          <p>{errorMessage}</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    // Show assessment results
    if (formData?.type === 'assessment' && assessmentResults) {
      // If showReport is false, show simple thank you without scores
      if (!assessmentResults.showReport) {
        return (
          <div className="public-feedback-container assessment-mode">
            <div className="assessment-brand-bar">
              <div className="assessment-brand-logo">
                <img src={MagnetixLogo} alt="Magnetix" className="assessment-brand-logo-img" />
              </div>
            </div>
            <div className="public-feedback-card assessment-results-card">
              <div className="assessment-thankyou-banner">
                <CheckCircle size={32} />
                <div className="assessment-thankyou-text">
                  <h2>Thank You for completing this assessment!</h2>
                  <p>Your answers have been recorded and are being evaluated. Results will be shared with you soon.</p>
                </div>
              </div>
              <div className="public-feedback-footer assessment-footer">
                <p>Magnetix Assessment Platform</p>
              </div>
            </div>
          </div>
        );
      }

      const isSubjectiveResult = assessmentResults.assessmentType === 'subjective' || assessmentResults.assessmentType === 'both';
      const displayScore = typeof assessmentResults.score === 'number' && assessmentResults.score % 1 !== 0
        ? assessmentResults.score.toFixed(2)
        : assessmentResults.score;
      const displayPercentage = typeof assessmentResults.percentage === 'number' && assessmentResults.percentage % 1 !== 0
        ? assessmentResults.percentage.toFixed(1)
        : assessmentResults.percentage;

      return (
        <div className="public-feedback-container assessment-mode">
          {/* Assessment branded header */}
          <div className="assessment-brand-bar">
            <div className="assessment-brand-logo">
              <img src={MagnetixLogo} alt="Magnetix" className="assessment-brand-logo-img" />
            </div>
          </div>

          <div className="public-feedback-card assessment-results-card">
            {/* Thank you banner */}
            <div className="assessment-thankyou-banner">
              <CheckCircle size={32} />
              <div className="assessment-thankyou-text">
                <h2>Thank You for completing this assessment!</h2>
                <p>{getScoreMessage(assessmentResults.percentage)}</p>
              </div>
            </div>

            <div className="assessment-results-header">
              <div className="assessment-score-display">
                <div className="assessment-score-circle">
                  <span className="assessment-score-value">{displayScore}/{assessmentResults.maxScore}</span>
                  <span className="assessment-score-percentage">{displayPercentage}%</span>
                </div>
              </div>
            </div>

            {/* AI Scored Badge for subjective */}
            {isSubjectiveResult && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                margin: '16px auto', padding: '10px 24px', maxWidth: '280px',
                backgroundColor: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe',
                fontSize: '14px', color: '#1e40af', fontWeight: '600',
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" />
                </svg>
                Scored by AI
              </div>
            )}

            {/* Respondent info if provided */}
            {(assessmentResults.respondentName || assessmentResults.respondentEmail) && (
              <div className="assessment-respondent-info">
                {assessmentResults.respondentName && (
                  <div className="assessment-respondent-item">
                    <User size={16} />
                    <span>{assessmentResults.respondentName}</span>
                  </div>
                )}
                {assessmentResults.respondentEmail && (
                  <div className="assessment-respondent-item">
                    <Mail size={16} />
                    <span>{assessmentResults.respondentEmail}</span>
                  </div>
                )}
              </div>
            )}

            {/* Questions with answers */}
            {assessmentResults.showCorrectAnswers && assessmentResults.results && (
              <div className="assessment-results-questions">
                {assessmentResults.results.map((result, index) => {
                  // Subjective question result with AI scoring
                  if (result.isSubjective) {
                    const scoreRatio = (result.aiScore != null && result.aiMaxScore)
                      ? (result.aiScore as number) / (result.aiMaxScore as number)
                      : 0;
                    const scoreColor = scoreRatio >= 0.7 ? '#166534' : scoreRatio >= 0.4 ? '#92400e' : '#991b1b';
                    const scoreBg = scoreRatio >= 0.7 ? '#dcfce7' : scoreRatio >= 0.4 ? '#fef3c7' : '#fee2e2';
                    const cardBorder = scoreRatio >= 0.7 ? '#bbf7d0' : scoreRatio >= 0.4 ? '#fde68a' : '#fecaca';
                    const cardBg = scoreRatio >= 0.7 ? '#f0fdf4' : scoreRatio >= 0.4 ? '#fffbeb' : '#fef2f2';

                    return (
                      <div key={result.questionId} style={{
                        padding: '16px', borderRadius: '10px', marginBottom: '12px',
                        border: `1px solid ${cardBorder}`, backgroundColor: cardBg,
                      }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
                          <span style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937', flex: 1 }}>
                            <span style={{ color: '#9ca3af', fontWeight: '600', marginRight: '8px' }}>Q{index + 1}.</span>
                            {result.questionText}
                          </span>
                          {result.aiScore != null && (
                            <span style={{
                              flexShrink: 0, marginLeft: '12px', padding: '4px 14px',
                              borderRadius: '20px', fontSize: '13px', fontWeight: '700',
                              backgroundColor: scoreBg, color: scoreColor,
                            }}>
                              {result.aiScore} / {result.aiMaxScore}
                            </span>
                          )}
                        </div>

                        {/* User's answer */}
                        <div style={{
                          marginBottom: '10px', padding: '10px 14px',
                          backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: '6px',
                          border: '1px solid #e5e7eb',
                        }}>
                          <div style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
                            Your Answer
                          </div>
                          <div style={{ fontSize: '14px', color: '#1f2937', whiteSpace: 'pre-wrap' }}>
                            {result.userAnswerText || 'No answer provided'}
                          </div>
                        </div>

                        {/* AI Feedback */}
                        {result.aiFeedback && (
                          <div style={{
                            padding: '10px 14px',
                            backgroundColor: '#eff6ff', borderRadius: '6px',
                            border: '1px solid #bfdbfe',
                          }}>
                            <div style={{
                              fontSize: '12px', fontWeight: '600', color: '#1e40af',
                              display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px',
                            }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 2L2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" />
                              </svg>
                              AI Feedback
                            </div>
                            <div style={{ fontSize: '13px', color: '#374151', lineHeight: '1.5' }}>
                              {result.aiFeedback}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  }

                  // Objective question result (existing)
                  return (
                    <div
                      key={result.questionId}
                      className={`assessment-result-item ${result.isCorrect ? 'correct' : 'incorrect'}`}
                    >
                      <div className="assessment-result-header">
                        <span className="assessment-question-number">Q{index + 1}</span>
                        <span className="assessment-question-text">{result.questionText}</span>
                        <span className={`assessment-result-badge ${result.isCorrect ? 'correct' : 'incorrect'}`}>
                          {result.isCorrect ? (
                            <><CheckCircle size={14} /> Correct</>
                          ) : (
                            <><XCircle size={14} /> Incorrect</>
                          )}
                        </span>
                      </div>
                      {result.options && result.options.length > 0 && (
                        <div className="assessment-options-list">
                          {result.options.map((option, optIdx) => {
                            const isUserSelected = result.userSelectedIndices?.includes(optIdx) || false;
                            const isCorrectAnswer = result.correctIndices?.includes(optIdx) || false;

                            let optionClass = 'assessment-option';
                            if (isCorrectAnswer) optionClass += ' correct-answer';
                            if (isUserSelected && !isCorrectAnswer) optionClass += ' wrong-selection';
                            if (isUserSelected && isCorrectAnswer) optionClass += ' correct-selection';

                            return (
                              <div key={optIdx} className={optionClass}>
                                <span className="assessment-option-text">{option}</span>
                                <div className="assessment-option-labels">
                                  {isUserSelected && (
                                    <span className="assessment-label user-answer">Your answer</span>
                                  )}
                                  {isCorrectAnswer && (
                                    <span className="assessment-label correct-label">Correct</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Score-only message when correct answers are hidden */}
            {!assessmentResults.showCorrectAnswers && (
              <div className="assessment-score-only-message">
                <Award size={20} />
                <p>Your results have been recorded. Detailed answers are not available for this assessment.</p>
              </div>
            )}

            {/* AI evaluation disclaimer for subjective/combined assessments */}
            {isSubjectiveResult && (
              <div style={{
                margin: '20px 0',
                padding: '14px 18px',
                backgroundColor: '#fffbeb',
                border: '1px solid #fde68a',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
              }}>
                <AlertCircle size={18} style={{ color: '#d97706', flexShrink: 0, marginTop: '1px' }} />
                <p style={{ margin: 0, fontSize: '13px', color: '#92400e', lineHeight: '1.5' }}>
                  This score has been evaluated by AI and is subject to change. Your instructor may review and adjust the scores.
                </p>
              </div>
            )}

            <div className="public-feedback-footer assessment-footer">
              <p>Magnetix Assessment Platform</p>
            </div>
          </div>
        </div>
      );
    }

    // Generic success message for non-assessment forms
    return (
      <div className="public-feedback-container">
        <div className="public-feedback-success">
          <CheckCircle size={64} />
          <h2>Thank You!</h2>
          <p>Your response has been submitted successfully.</p>
        </div>
      </div>
    );
  }

  if (!formData) return null;

  return (
    <div className={`public-feedback-container ${isAssessment ? 'assessment-mode' : ''}`}>
      {/* Assessment branded header bar */}
      {isAssessment && (
        <div className="assessment-brand-bar">
          <div className="assessment-brand-logo">
            <img src={MagnetixLogo} alt="Magnetix" className="assessment-brand-logo-img" />
          </div>
          <div className="assessment-brand-meta">
            <span>{totalQuestions} Questions</span>
          </div>
        </div>
      )}

      <div className="public-feedback-card">
        <div className="public-feedback-header">
          {isAssessment && <span className="assessment-type-badge">Assessment</span>}
          <h1>{formData.name}</h1>
          {formData.description && <p>{formData.description}</p>}
        </div>

        {/* Assessment progress bar */}
        {isAssessment && (
          <div className="assessment-progress-container">
            <div
              className="assessment-progress-fill"
              style={{ width: `${((currentQuestionIndex + 1) / totalQuestions) * 100}%` }}
            />
          </div>
        )}

        <form onSubmit={handleSubmit} className="public-feedback-form">
          {/* Question counter for assessment stepper */}
          {isAssessment && (
            <div className="assessment-question-counter">
              Question {currentQuestionIndex + 1} of {totalQuestions}
            </div>
          )}

          {/* Respondent info: always for non-assessment, step 0 only for assessment */}
          {(formData.collectName || formData.collectEmail) && (!isAssessment || currentQuestionIndex === 0) && (
            <div className="public-feedback-respondent-info">
              {formData.collectName && (
                <div className="public-feedback-field">
                  <label>Your Name <span className="public-feedback-required">*</span></label>
                  <input
                    type="text"
                    className="public-feedback-input"
                    placeholder="Enter your name"
                    value={respondentName}
                    onChange={(e) => setRespondentName(e.target.value)}
                  />
                </div>
              )}
              {formData.collectEmail && (
                <div className="public-feedback-field">
                  <label>Your Email <span className="public-feedback-required">*</span></label>
                  <input
                    type="email"
                    className="public-feedback-input"
                    placeholder="Enter your email"
                    value={respondentEmail}
                    onChange={(e) => setRespondentEmail(e.target.value)}
                  />
                </div>
              )}
            </div>
          )}

          {/* Questions: one at a time for assessment, all at once for feedback/satisfaction */}
          <div className="public-feedback-questions">
            {isAssessment
              ? renderQuestion(formData.questions[currentQuestionIndex], currentQuestionIndex)
              : formData.questions.map((question, index) => renderQuestion(question, index))
            }
          </div>

          {errorMessage && (
            <div className="public-feedback-error-message">
              <AlertCircle size={16} />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* Assessment stepper navigation */}
          {isAssessment ? (
            <div className="assessment-nav-buttons">
              <button
                type="button"
                className="assessment-nav-btn secondary"
                onClick={handlePrevious}
                disabled={currentQuestionIndex === 0}
              >
                <ChevronLeft size={18} />
                Previous
              </button>

              {currentQuestionIndex < totalQuestions - 1 ? (
                <button
                  type="button"
                  className="assessment-nav-btn primary"
                  onClick={handleNext}
                >
                  Next
                  <ChevronRight size={18} />
                </button>
              ) : (
                <button
                  type="submit"
                  className="assessment-nav-btn primary"
                  disabled={status === 'submitting'}
                >
                  {status === 'submitting' ? (
                    <>
                      <Loader2 size={18} className="public-feedback-spinner" />
                      {formData?.assessmentType === 'subjective' ? 'AI is scoring...' : 'Submitting...'}
                    </>
                  ) : (
                    'Submit Assessment'
                  )}
                </button>
              )}
            </div>
          ) : (
            <button
              type="submit"
              className="public-feedback-submit"
              disabled={status === 'submitting'}
            >
              {status === 'submitting' ? (
                <>
                  <Loader2 size={20} className="public-feedback-spinner" />
                  Submitting...
                </>
              ) : (
                'Submit Response'
              )}
            </button>
          )}
        </form>

        <div className={`public-feedback-footer ${isAssessment ? 'assessment-footer' : ''}`}>
          <p>{isAssessment ? 'Magnetix Assessment Platform' : 'Powered by Magnetix'}</p>
        </div>
      </div>
    </div>
  );
};

export default PublicFeedbackForm;
